import { CollapsibleFilters } from '../components/common/CollapsibleFilters';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemContent,
  ListItemDecorator,
  Stack,
  Divider,
  Select,
  Option,
  FormControl,
  FormLabel,
  Switch,
  Sheet,
} from '@mui/joy';
import {
  ChatCircle as CommentIcon,
  ClipboardText as TaskIcon,
  Briefcase as JobIcon,
  Info as InfoIcon,
  Trash as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Bell as NotificationsIcon,
  Mailbox as UnreadIcon,
  BellRinging as PushIcon,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { useNotificationPage, useUnreadNotificationCount, useNotificationMutations } from '../hooks/useNotifications';
import type { Notification } from '../hooks/useNotifications';
import { authService } from '../lib/auth';
import { PageHelpButton } from '../components/knowledge-base/PageHelpButton';

interface PushPreferences {
  chat_messages: boolean;
  chat_mentions: boolean;
  chat_dms: boolean;
  task_assigned: boolean;
  task_completed: boolean;
  job_updates: boolean;
  inbox_items: boolean;
  system_alerts: boolean;
  quiet_hours_enabled: boolean;
}

const PUSH_PREF_LABELS: Record<string, string> = {
  chat_messages: 'Channel Messages',
  chat_mentions: 'Mentions',
  chat_dms: 'Direct Messages',
  task_assigned: 'Task Assigned',
  task_completed: 'Task Completed',
  job_updates: 'Job Updates',
  inbox_items: 'Inbox Items',
  system_alerts: 'System Alerts',
};

const DEFAULT_PUSH_PREFS: PushPreferences = {
  chat_messages: true,
  chat_mentions: true,
  chat_dms: true,
  task_assigned: true,
  task_completed: true,
  job_updates: true,
  inbox_items: false,
  system_alerts: true,
  quiet_hours_enabled: false,
};

function usePushPreferences() {
  const [prefs, setPrefs] = useState<PushPreferences>(DEFAULT_PUSH_PREFS);
  const [loading, setLoading] = useState(true);

  const fetchPrefs = useCallback(async () => {
    try {
      const accessToken = authService.getAccessToken();
      const res = await fetch('/api/devices/preferences/', {
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
        },
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setPrefs({ ...DEFAULT_PUSH_PREFS, ...data });
      }
    } catch {
      // Use defaults
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPrefs();
  }, [fetchPrefs]);

  const updatePref = useCallback(
    async (key: keyof PushPreferences, value: boolean) => {
      const prev = prefs;
      setPrefs((p) => ({ ...p, [key]: value }));
      try {
        const accessToken = authService.getAccessToken();
        await fetch('/api/devices/preferences/', {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
          },
          credentials: 'include',
          body: JSON.stringify({ [key]: value }),
        });
      } catch {
        setPrefs(prev);
      }
    },
    [prefs],
  );

  return { prefs, loading, updatePref };
}

export const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');

  const [page, setPage] = useState(1);
  const { data, isLoading, isError, refetch } = useNotificationPage(page, filter === 'unread', typeFilter);
  const notifications = data?.results || [];
  const { data: unreadCount = 0 } = useUnreadNotificationCount();
  const { markAsRead, markAllAsRead, deleteNotification } = useNotificationMutations();
  const { prefs: pushPrefs, loading: pushLoading, updatePref } = usePushPreferences();

  const browserPushSupported = 'serviceWorker' in navigator && 'PushManager' in window;
  const [browserPermission, setBrowserPermission] = useState<NotificationPermission | null>(null);

  useEffect(() => {
    if (browserPushSupported) {
      setBrowserPermission(Notification.permission);
    }
  }, [browserPushSupported]);

  const getNotificationIcon = (type: Notification['notification_type']) => {
    switch (type) {
      case 'mention':
      case 'comment':
        return <CommentIcon />;
      case 'task':
        return <TaskIcon />;
      case 'job':
        return <JobIcon />;
      default:
        return <InfoIcon />;
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    // Mark as read
    if (!notification.is_read) {
      markAsRead.mutate(notification.id);
    }

    // Navigate to related content
    if (notification.related_url) {
      if (notification.related_url.startsWith('/')) navigate(notification.related_url);
      else window.location.href = notification.related_url;
    }
  };

  const handleDelete = (e: React.MouseEvent, notificationId: number) => {
    e.stopPropagation();
    deleteNotification.mutate(notificationId, { onSuccess: () => {
      if (notifications.length === 1 && page > 1) setPage(page - 1);
    } });
  };

  const handleMarkAsRead = (e: React.MouseEvent, notification: Notification) => {
    e.stopPropagation();
    if (!notification.is_read) {
      markAsRead.mutate(notification.id);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography level="h2" startDecorator={<NotificationsIcon />} sx={{ fontSize: { xs: '1.5rem', md: undefined } }}>
            Notifications
          </Typography>
          <PageHelpButton />
        </Box>
        {unreadCount > 0 && (
          <Button
            variant="soft"
            color="primary"
            startDecorator={<CheckCircleIcon />}
            onClick={() => markAllAsRead.mutate(undefined, { onSuccess: () => setPage(1) })}
          >
            Mark all as read
          </Button>
        )}
      </Box>

      {/* Stats */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Card variant="soft" sx={{ flex: 1 }}>
          <CardContent>
            <Typography level="body-sm">{filter === 'all' && typeFilter === 'all' ? 'Total' : 'Matching notifications'}</Typography>
            <Typography level="h3">{data?.count ?? '—'}</Typography>
          </CardContent>
        </Card>
        <Card variant="soft" color="primary" sx={{ flex: 1 }}>
          <CardContent>
            <Typography level="body-sm">Unread</Typography>
            <Typography level="h3">{unreadCount}</Typography>
          </CardContent>
        </Card>
      </Stack>

      {/* Push Notification Preferences */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography level="title-md" startDecorator={<PushIcon />} sx={{ mb: 2 }}>
            Push Notification Preferences
          </Typography>

          {browserPushSupported && browserPermission !== 'granted' && (
            <Sheet variant="soft" color="primary" sx={{ p: 2, borderRadius: 'sm', mb: 2 }}>
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                <Typography level="body-sm">
                  Enable browser notifications to receive push alerts on this device.
                </Typography>
                <Button
                  size="sm"
                  onClick={async () => {
                    const perm = await Notification.requestPermission();
                    setBrowserPermission(perm);
                  }}
                >
                  Enable
                </Button>
              </Stack>
            </Sheet>
          )}

          {!pushLoading && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 1.5,
              }}
            >
              {(Object.keys(PUSH_PREF_LABELS) as (keyof PushPreferences)[]).map((key) => (
                <Stack
                  key={key}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ py: 0.5 }}
                >
                  <Typography level="body-sm">{PUSH_PREF_LABELS[key]}</Typography>
                  <Switch
                    checked={pushPrefs[key] as boolean}
                    onChange={(e) => updatePref(key, e.target.checked)}
                    size="sm"
                  />
                </Stack>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Filters */}
      <CollapsibleFilters><Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <FormControl sx={{ flex: 1 }}>
          <FormLabel>Status</FormLabel>
          <Select value={filter} onChange={(_, value) => { setFilter(value as 'all' | 'unread'); setPage(1); }}>
            <Option value="all">All</Option>
            <Option value="unread">Unread only</Option>
          </Select>
        </FormControl>
        <FormControl sx={{ flex: 1 }}>
          <FormLabel>Type</FormLabel>
          <Select value={typeFilter} onChange={(_, value) => { setTypeFilter(value as string); setPage(1); }}>
            <Option value="all">All types</Option>
            <Option value="mention">Mentions</Option>
            <Option value="comment">Comments</Option>
            <Option value="task_assigned">Task assignments</Option>
            <Option value="task_completed">Completed tasks</Option>
            <Option value="system">System alerts</Option>
            <Option value="dev_update">Development updates</Option>
          </Select>
        </FormControl>
      </Stack></CollapsibleFilters>

      {/* Notifications List */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {isLoading ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography level="body-sm">Loading notifications...</Typography>
            </Box>
          ) : isError ? (
            <Box sx={{ p: 3 }}>
              <Typography role="alert">Could not load notifications.</Typography>
              <Button variant="plain" onClick={() => refetch()}>Try again</Button>
              {page > 1 && <Button variant="plain" onClick={() => setPage(1)}>Back to latest</Button>}
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <UnreadIcon size={48} color="var(--joy-palette-text-tertiary)" style={{ marginBottom: 16 }} />
              <Typography level="body-sm">
                {filter === 'unread' ? 'No unread notifications match these filters' : 'No notifications match these filters'}
              </Typography>
            </Box>
          ) : (
            <List>
              {notifications.map((notification, index) => (
                <React.Fragment key={notification.id}>
                  <ListItem>
                    <ListItemButton
                      onClick={() => handleNotificationClick(notification)}
                      sx={{
                        backgroundColor: notification.is_read ? 'transparent' : 'background.level1',
                      }}
                    >
                      <ListItemDecorator>
                        {getNotificationIcon(notification.notification_type)}
                      </ListItemDecorator>
                      <ListItemContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              level="body-md"
                              sx={{ fontWeight: notification.is_read ? 'normal' : 'bold' }}
                            >
                              {notification.title}
                            </Typography>
                            <Typography level="body-sm" sx={{ mt: 0.5 }}>
                              {notification.body}
                            </Typography>
                            <Stack direction="row" spacing={1} sx={{ mt: 1, alignItems: 'center' }}>
                              <Typography level="body-xs" color="neutral">
                                {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                              </Typography>
                              {notification.created_by_name && (
                                <>
                                  <Typography level="body-xs" color="neutral">•</Typography>
                                  <Typography level="body-xs" color="neutral">
                                    from {notification.created_by_name}
                                  </Typography>
                                </>
                              )}
                              {!notification.is_read && (
                                <Chip size="sm" variant="soft" color="primary">New</Chip>
                              )}
                            </Stack>
                          </Box>
                          <Stack direction="row" spacing={1}>
                            {!notification.is_read && (
                              <IconButton
                                aria-label={`Mark ${notification.title} as read`}
                                size="sm"
                                variant="plain"
                                color="neutral"
                                onClick={(e) => handleMarkAsRead(e, notification)}
                              >
                                <CheckCircleIcon />
                              </IconButton>
                            )}
                            <IconButton
                              aria-label={`Delete ${notification.title}`}
                              size="sm"
                              variant="plain"
                              color="danger"
                              onClick={(e) => handleDelete(e, notification.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Stack>
                        </Box>
                      </ListItemContent>
                    </ListItemButton>
                  </ListItem>
                  {index < notifications.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 2 }}>
        <Typography level="body-sm">Page {page} · {data?.count ?? '—'} {filter === 'all' && typeFilter === 'all' ? 'total' : 'matching'}</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" disabled={!data?.previous || isLoading} onClick={() => setPage(p => p - 1)}>Previous</Button>
          <Button variant="outlined" disabled={!data?.next || isLoading} onClick={() => setPage(p => p + 1)}>Next</Button>
        </Stack>
      </Stack>
    </Box>
  );
};
